import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { patientAPI, woundAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Plus, ChevronRight, Stethoscope, FileText, Calendar, MapPin, AlertTriangle, TrendingDown, TrendingUp, Minus, ClipboardList, BookOpen, Archive, ArchiveRestore, BarChart3, Pencil, Trash2 } from 'lucide-react';
import PatientStatistics from '@/components/PatientStatistics';
import { toast } from 'sonner';

export default function PatientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canArchive, isAdmin } = useAuth();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadPatient(); }, [id]);

  const loadPatient = async () => {
    try {
      const res = await patientAPI.get(id);
      setPatient(res.data);
    } catch { navigate('/patienten'); }
    finally { setLoading(false); }
  };

  const handleDeleteWound = async (e, woundId, wundart) => {
    e.stopPropagation();
    if (!window.confirm(`Wunde "${wundart}" wirklich löschen?\n\nAlle zugehörigen Berichte werden ebenfalls gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    try {
      await woundAPI.delete(woundId);
      toast.success('Wunde gelöscht');
      loadPatient();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const calcAge = (dob) => {
    if (!dob) return '';
    return Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  };

  const getWoundTrend = (wound) => {
    const lr = wound.latest_report;
    if (!lr) return null;
    const signs = lr.infektionszeichen || [];
    if (signs.length > 0) return { icon: AlertTriangle, color: 'text-rose-600', bg: 'bg-rose-50', label: 'Verschlechterung' };
    if (lr.schmerz_nrs >= 5) return { icon: Minus, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Stagnation' };
    return { icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Heilungsfortschritt' };
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 space-y-4">
        <div className="h-32 bg-slate-100 rounded-2xl animate-pulse" />
        <div className="h-48 bg-slate-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!patient) return null;

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-4" data-testid="patient-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate('/patienten')} className="p-2 rounded-xl" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-heading font-bold text-slate-900 truncate">{patient.name}</h1>
          <p className="text-sm text-slate-500">
            {calcAge(patient.geburtsdatum)} Jahre &middot; PG {patient.pflegegrad || '-'}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/patienten/${id}/bearbeiten`)} className="h-9 px-3 rounded-xl border-slate-200" data-testid="edit-patient-btn">
          <Pencil className="w-4 h-4 mr-1.5" />
          <span className="hidden sm:inline">Bearbeiten</span>
        </Button>
        <Badge variant="outline" className={`border shrink-0 ${patient.status === 'aktiv' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
          {patient.status === 'aktiv' ? 'Aktiv' : patient.status}
        </Badge>
      </div>

      {/* Patient Info Card */}
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Geburtsdatum</p>
              <p className="text-slate-700 mt-0.5">{new Date(patient.geburtsdatum).toLocaleDateString('de-DE')}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Pflegegrad</p>
              <p className="text-slate-700 mt-0.5 font-medium">{patient.pflegegrad || 'Keiner'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Versicherung</p>
              <p className="text-slate-700 mt-0.5">{patient.versicherung || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Telefon</p>
              <p className="text-slate-700 mt-0.5">{patient.telefon || '-'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Adresse</p>
              <p className="text-slate-700 mt-0.5">{patient.adresse || '-'}</p>
            </div>
          </div>
          {patient.notizen && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Notizen</p>
              <p className="text-sm text-slate-600 mt-1">{patient.notizen}</p>
            </div>
          )}
          {patient.diagnosen && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Diagnosen (Wundbezug)</p>
              <p className="text-sm text-slate-600 mt-1">{patient.diagnosen}</p>
            </div>
          )}
          {patient.versorgungsbeginn && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Versorgungsbeginn</p>
              <p className="text-sm text-slate-600 mt-1">{new Date(patient.versorgungsbeginn).toLocaleDateString('de-DE')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Action Buttons */}
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => navigate(`/patienten/${id}/anamnese`)} className="h-11 rounded-xl border-teal-200 text-teal-700 hover:bg-teal-50" data-testid="anamnese-btn">
          <ClipboardList className="w-4 h-4 mr-2" />Anamnese
        </Button>
        <Button variant="outline" onClick={() => navigate(`/patienten/${id}/beratung`)} className="h-11 rounded-xl border-teal-200 text-teal-700 hover:bg-teal-50" data-testid="beratung-btn">
          <BookOpen className="w-4 h-4 mr-2" />Beratung
        </Button>
      </div>
      {canArchive && patient.status !== 'archiviert' && (
        <Button variant="outline" onClick={async () => { if(window.confirm('Patient archivieren?')) { try { await patientAPI.archive(id); toast.success('Patient archiviert'); navigate('/patienten'); } catch { toast.error('Fehler'); }}}} className="w-full h-11 rounded-xl border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="archive-patient-btn">
          <Archive className="w-4 h-4 mr-2" />Patient archivieren
        </Button>
      )}
      {canArchive && patient.status === 'archiviert' && (
        <Button variant="outline" onClick={async () => { try { await patientAPI.restore(id); toast.success('Patient wiederhergestellt'); loadPatient(); } catch { toast.error('Fehler'); }}} className="w-full h-11 rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="restore-patient-btn">
          <ArchiveRestore className="w-4 h-4 mr-2" />Patient wiederherstellen
        </Button>
      )}

      {/* Tabs */}
      <Tabs defaultValue="wunden" className="w-full">
        <TabsList className="w-full bg-slate-100 rounded-xl p-1 h-auto">
          <TabsTrigger value="wunden" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium" data-testid="tab-wunden">
            Wunden ({patient.wounds?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="statistik" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium" data-testid="tab-statistik">
            <BarChart3 className="w-3.5 h-3.5 mr-1" />Statistik
          </TabsTrigger>
          <TabsTrigger value="info" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium" data-testid="tab-info">
            Details
          </TabsTrigger>
        </TabsList>

        <TabsContent value="wunden" className="mt-4 space-y-3">
          <Button
            data-testid="add-wound-btn"
            onClick={() => navigate(`/patienten/${id}/neue-wunde`)}
            className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neue Wunde dokumentieren
          </Button>

          {patient.wounds?.length > 0 ? (
            patient.wounds.map((w) => {
              const trend = getWoundTrend(w);
              const TrendIcon = trend?.icon || Minus;
              return (
                <Card
                  key={w.id}
                  className="border border-slate-200 shadow-sm hover:shadow-md hover:border-teal-200 transition-all cursor-pointer"
                  onClick={() => navigate(`/wunde/${w.id}`)}
                  data-testid={`wound-card-${w.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl ${trend?.bg || 'bg-slate-100'} flex items-center justify-center shrink-0`}>
                        <TrendIcon className={`w-5 h-5 ${trend?.color || 'text-slate-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{w.wundart}</p>
                          <Badge variant="outline" className={`text-[10px] border ${w.status === 'aktiv' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                            {w.status === 'aktiv' ? 'Offen' : 'Verheilt'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{w.lokalisation}</span>
                          <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{w.report_count || 0} Berichte</span>
                        </div>
                        {w.latest_report && (
                          <div className="flex items-center gap-3 mt-2 text-xs">
                            <span className="text-slate-500">
                              Letzte Fläche: <span className="font-medium text-slate-700">{w.latest_report.flaeche} cm²</span>
                            </span>
                            <span className="text-slate-500">
                              NRS: <span className="font-medium text-slate-700">{w.latest_report.schmerz_nrs}/10</span>
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 mt-2">
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteWound(e, w.id, w.wundart)}
                            className="h-8 w-8 p-0 text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                            data-testid={`delete-wound-${w.id}`}
                            title="Wunde löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-300" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Stethoscope className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Keine Wunden dokumentiert</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="statistik" className="mt-4">
          <PatientStatistics patientId={id} />
        </TabsContent>

        <TabsContent value="info" className="mt-4">
          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-4 space-y-4">
              <div>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Telefon</p>
                <p className="text-sm text-slate-700 mt-1">{patient.telefon || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Vollständige Adresse</p>
                <p className="text-sm text-slate-700 mt-1">{patient.adresse || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Angelegt am</p>
                <p className="text-sm text-slate-700 mt-1">
                  {patient.created_at ? new Date(patient.created_at).toLocaleDateString('de-DE') : '-'}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
